import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle2, Loader2, ArrowRight, MapPin } from "lucide-react";
import { API, LOCALITIES } from "../constants";

const inputCls =
    "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3.5 text-sm sm:text-base text-slate-900 placeholder:text-slate-400 outline-none transition-all duration-200 focus:border-[#0B5CF5] focus:ring-4 focus:ring-[#0B5CF5]/10";

const labelCls = "block text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-2";

const formatError = (detail) => {
    if (!detail) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
    return String(detail);
};

const SUBMITTED_KEY = "sevano_pilot_enquiry_submitted";

const readSubmitted = () => {
    try {
        return window.localStorage.getItem(SUBMITTED_KEY) || "";
    } catch {
        return "";
    }
};

const PilotEnquiryModal = ({ open, onClose }) => {
    const [form, setForm] = useState({ locality: "", name: "", mobile: "", email: "" });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    // "created" | "existing" | "" — persisted so refresh/reopen shows the Thank You screen
    const [success, setSuccess] = useState(readSubmitted);
    const inFlight = useRef(false);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        if (open) window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    useEffect(() => {
        if (!open) {
            setError("");
            setLoading(false);
            setTimeout(() => {
                setSuccess(readSubmitted());
                setForm({ locality: "", name: "", mobile: "", email: "" });
            }, 300);
        } else {
            setSuccess(readSubmitted());
        }
    }, [open]);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (inFlight.current) return; // block double-click / double-submit
        inFlight.current = true;
        setError("");
        setLoading(true);
        try {
            const payload = { locality: form.locality, name: form.name, mobile: form.mobile };
            if (form.email.trim()) payload.email = form.email.trim();
            const { data } = await axios.post(`${API}/pilot-enquiries`, payload);
            const status = data?.status === "existing" ? "existing" : "created";
            try {
                window.localStorage.setItem(SUBMITTED_KEY, status);
            } catch {
                /* storage unavailable — backend still prevents duplicates */
            }
            setSuccess(status);
        } catch (err) {
            setError(formatError(err.response?.data?.detail));
        } finally {
            inFlight.current = false;
            setLoading(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-6"
                    data-testid="pilot-enquiry-modal"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Request a service"
                >
                    <div
                        className="absolute inset-0 bg-[#0F172A]/55 backdrop-blur-md"
                        onClick={onClose}
                        data-testid="pilot-modal-backdrop"
                    />
                    <motion.div
                        initial={{ opacity: 0, y: 48, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 32, scale: 0.98 }}
                        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                        className="relative w-full sm:max-w-md rounded-t-[2rem] sm:rounded-[2rem] bg-white p-6 sm:p-8 shadow-[0_40px_90px_-30px_rgba(11,92,245,0.5)] max-h-[92vh] overflow-y-auto"
                    >
                        <button
                            onClick={onClose}
                            data-testid="pilot-close-button"
                            aria-label="Close"
                            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 transition-colors"
                        >
                            <X size={17} />
                        </button>

                        {success ? (
                            <div className="text-center py-8" data-testid="pilot-success-message">
                                <div className="w-16 h-16 mx-auto rounded-full bg-[#EBF2FF] flex items-center justify-center mb-5">
                                    <CheckCircle2 size={32} className="text-[#0B5CF5]" />
                                </div>
                                <h3 className="text-2xl font-extrabold text-slate-900 tracking-tight">
                                    {success === "existing" ? "You're already registered" : "Request received"}
                                </h3>
                                <p className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed max-w-sm mx-auto">
                                    Thank you for your interest in Sevano! We're preparing to launch in selected
                                    localities. Our team will contact you when services become available in your
                                    area.
                                </p>
                                {success === "existing" && (
                                    <p
                                        className="mt-3 text-xs sm:text-sm text-slate-500 max-w-sm mx-auto"
                                        data-testid="pilot-existing-note"
                                    >
                                        We already have your enquiry on file — no need to submit again.
                                    </p>
                                )}
                                <button
                                    onClick={onClose}
                                    data-testid="pilot-success-close-button"
                                    className="btn-primary mt-7 px-7 py-3 text-sm"
                                >
                                    Done
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="mb-6">
                                    <div className="inline-flex items-center gap-2 rounded-full bg-[#EBF2FF] px-3 py-1 mb-4">
                                        <MapPin size={12} className="text-[#0B5CF5]" />
                                        <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#0B5CF5]">
                                            Pilot enquiry — Agartala
                                        </span>
                                    </div>
                                    <h3 className="font-display font-medium text-2xl text-slate-900 tracking-tight">
                                        Request a Service
                                    </h3>
                                    <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                                        This is an early enquiry, not a confirmed booking. We'll reach out as soon
                                        as Sevano opens in your locality.
                                    </p>
                                </div>

                                <form onSubmit={submit} className="flex flex-col gap-4" data-testid="pilot-enquiry-form" noValidate>
                                    <div>
                                        <label htmlFor="pe-locality" className={labelCls}>
                                            Location / Locality
                                        </label>
                                        <select
                                            id="pe-locality"
                                            data-testid="pilot-locality-select"
                                            required
                                            value={form.locality}
                                            onChange={set("locality")}
                                            className={`${inputCls} ${form.locality ? "" : "text-slate-400"}`}
                                        >
                                            <option value="" disabled>
                                                Select your locality
                                            </option>
                                            {LOCALITIES.map((l) => (
                                                <option key={l} value={l}>
                                                    {l}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor="pe-name" className={labelCls}>
                                            Full name
                                        </label>
                                        <input
                                            id="pe-name"
                                            data-testid="pilot-name-input"
                                            type="text"
                                            required
                                            value={form.name}
                                            onChange={set("name")}
                                            placeholder="e.g. Rahul Saha"
                                            className={inputCls}
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="pe-mobile" className={labelCls}>
                                            Mobile number
                                        </label>
                                        <input
                                            id="pe-mobile"
                                            data-testid="pilot-mobile-input"
                                            type="tel"
                                            required
                                            value={form.mobile}
                                            onChange={set("mobile")}
                                            placeholder="+91 98765 43210"
                                            className={inputCls}
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="pe-email" className={labelCls}>
                                            Email address{" "}
                                            <span className="normal-case font-medium text-slate-400">(optional)</span>
                                        </label>
                                        <input
                                            id="pe-email"
                                            data-testid="pilot-email-input"
                                            type="email"
                                            value={form.email}
                                            onChange={set("email")}
                                            placeholder="rahulsaha@example.com"
                                            className={inputCls}
                                        />
                                    </div>

                                    <AnimatePresence>
                                        {error && (
                                            <motion.p
                                                initial={{ opacity: 0, y: -6 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0 }}
                                                className="text-sm font-semibold text-red-600 bg-red-50 rounded-xl px-4 py-3"
                                                data-testid="pilot-error-message"
                                                role="alert"
                                            >
                                                {error}
                                            </motion.p>
                                        )}
                                    </AnimatePresence>

                                    <button
                                        type="submit"
                                        data-testid="pilot-submit-button"
                                        disabled={loading}
                                        className="btn-primary inline-flex items-center justify-center gap-2 px-7 py-4 text-sm sm:text-base disabled:opacity-60 disabled:cursor-not-allowed mt-1"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 size={18} className="animate-spin" /> Sending…
                                            </>
                                        ) : (
                                            <>
                                                Submit Enquiry <ArrowRight size={18} />
                                            </>
                                        )}
                                    </button>
                                    <p className="text-[11px] text-slate-400 text-center leading-relaxed">
                                        Your details are used only to contact you about the Sevano launch in your
                                        area.
                                    </p>
                                </form>
                            </>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default PilotEnquiryModal;
